/**
 * Phase 11 Plan 05 — hook that returns a Map<clientId, BillingState> derived
 * from clients.billing_mode + latest fanbasis_subscriptions row +
 * payment_methods row count + unresolved dispute.created events.
 *
 * One bulk query per table; memoized map keyed by clientId. Used by CommandCenter
 * to render the BillingStatusBadge and gate the Migrate button without N+1
 * queries per row.
 */
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface ClientBillingState {
  billing_mode: 'legacy_cron' | 'subscription' | 'direct_charge';
  subscription_status?: 'active' | 'past_due' | 'canceled' | 'completed' | null;
  has_payment_method: boolean;
  has_unresolved_dispute: boolean;
}

export function useClientBillingStates() {
  return useQuery({
    queryKey: ['client_billing_states'],
    queryFn: async () => {
      const anyClient = supabase as unknown as {
        from: (t: string) => any;
      };

      const [clientsRes, subsRes, pmRes, disputeRes] = await Promise.all([
        anyClient.from('clients').select('id, billing_mode'),
        anyClient
          .from('fanbasis_subscriptions')
          .select('client_id, status, started_at')
          .order('started_at', { ascending: false }),
        anyClient
          .from('payment_methods')
          .select('client_id, status')
          .eq('status', 'active'),
        anyClient
          .from('fanbasis_events')
          .select('client_id, event_type, received_at')
          .in('event_type', ['dispute.created', 'dispute.updated'])
          .order('received_at', { ascending: false }),
      ]);

      const latestSubByClient = new Map<string, string>();
      for (const row of (subsRes.data ?? []) as Array<{
        client_id: string;
        status: string;
      }>) {
        if (!latestSubByClient.has(row.client_id)) {
          latestSubByClient.set(row.client_id, row.status);
        }
      }

      const pmByClient = new Set<string>();
      for (const row of (pmRes.data ?? []) as Array<{ client_id: string }>) {
        pmByClient.add(row.client_id);
      }

      // Unresolved dispute: latest event per client — if event_type is
      // `dispute.created` (and no later `dispute.updated`), it is unresolved.
      const latestDisputeType = new Map<string, string>();
      for (const row of (disputeRes.data ?? []) as Array<{
        client_id: string | null;
        event_type: string;
      }>) {
        if (!row.client_id) continue;
        if (!latestDisputeType.has(row.client_id)) {
          latestDisputeType.set(row.client_id, row.event_type);
        }
      }

      const out = new Map<string, ClientBillingState>();
      for (const row of (clientsRes.data ?? []) as Array<{
        id: string;
        billing_mode: string | null;
      }>) {
        const mode = (row.billing_mode ?? 'legacy_cron') as ClientBillingState['billing_mode'];
        out.set(row.id, {
          billing_mode: mode,
          subscription_status: (latestSubByClient.get(row.id) as ClientBillingState['subscription_status']) ?? null,
          has_payment_method: pmByClient.has(row.id),
          has_unresolved_dispute:
            latestDisputeType.get(row.id) === 'dispute.created',
        });
      }
      return out;
    },
    staleTime: 30_000,
  });
}
